import * as React from "react";
import { useParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Card,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../../../utils/types/user";
import { useFetchUserDetails } from "../../../../../hooks/queries/user";
import useToast from "../../../../../hooks/useToast";
import { formatDate } from "../../../../../utils/helpers";
import { COMMUNICATION_HISTORY_DATE_FORMAT } from "../../../../../utils/constants";
import EditIcon from "../../../../../icons/Edit";
import DeleteIcon from "../../../../../icons/Delete";
import {
    CommunicationNoteChannel,
    CommunicationNoteOriginSystem,
    ICommunicationNote,
} from "../../../types/clients.types";
import { useDeleteCommunicationNote } from "../../../hooks/clients.mutations";
import AddCommunicationNote from "../AddCommunicationNote/AddCommunicationNote";
import EditCommunicationNote from "../EditCommunicationNote/EditCommunicationNote";

interface CommunicationNotesProps {
    channel: CommunicationNoteChannel;
    recordId: string;
    notes: ICommunicationNote[];
    canAddNote: boolean;
    onClose(): void;
}

export default function CommunicationNotes({
    channel,
    recordId,
    notes,
    canAddNote,
    onClose,
}: CommunicationNotesProps) {
    const [noteInEdit, setNoteInEdit] =
        React.useState<ICommunicationNote | null>(null);
    const [noteToDeleteId, setNoteToDeleteId] = React.useState<string | null>(
        null
    );

    const params = useParams();
    const clientId = params.clientId as string;

    const isSms = channel === CommunicationNoteChannel.SMS;

    const canWrite = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_COMMUNICATIONS_WRITE,
    ]);
    const isAdmin = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
    ]);

    const loggedInUser = useFetchUserDetails();

    const addIsMutating = useIsMutating(["add-communication-note", clientId]);
    const editIsMutating = useIsMutating(["edit-communication-note", clientId]);

    const deleteCommunicationNote = useDeleteCommunicationNote(clientId);

    const queryClient = useQueryClient();
    const { toast } = useToast();

    const canModifyNote = (note: ICommunicationNote) =>
        canWrite &&
        (isAdmin ||
            loggedInUser.data?.user_email?.toLowerCase() ===
                note.author_email.toLowerCase());

    const handleNotesChange = () => {
        queryClient.invalidateQueries({
            queryKey: [clientId, isSms ? "sms-history" : "call-history"],
        });
    };

    const handleEditComplete = () => {
        handleNotesChange();
        setNoteInEdit(null);
    };

    const handleDeleteNote = () => {
        if (!noteToDeleteId) return;

        deleteCommunicationNote.mutate(noteToDeleteId, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Note deleted successfully",
                });
                handleNotesChange();
                setNoteToDeleteId(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Cannot delete note at this moment, please try again",
                });
            },
        });
    };

    return (
        <>
            <Dialog open={!noteInEdit}>
                <DialogContent
                    title={isSms ? "SMS notes" : "Call notes"}
                    handleCloseDialog={onClose}
                    showFooter={canAddNote}
                    saveText="Add note"
                    cancelText="Close"
                    isSubmitBtnDisabled={addIsMutating > 0}
                    isCancelBtnDisabled={addIsMutating > 0}
                    submitBtnFormValue="add-communication-note"
                >
                    <>
                        <p className="text-caption text-textSec mb-[--spacing-items]">
                            Notes are shared with Intake and are the same on
                            this client&apos;s chart in every practice.
                        </p>
                        {notes.length > 0 ? (
                            <div className="flex flex-col gap-y-[--spacing-items] mb-[--spacing-header]">
                                {notes.map((note) => (
                                    <Card
                                        key={note.communication_note_id}
                                        classNames="flex flex-col gap-y-2"
                                    >
                                        <div className="flex items-start justify-between gap-x-[--spacing-items]">
                                            <div className="flex flex-col gap-y-1">
                                                <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                                                    <span className="text-base font-medium">
                                                        {note.author_name}
                                                    </span>
                                                    {note.origin_system ===
                                                    CommunicationNoteOriginSystem.INTAKE ? (
                                                        <Badge
                                                            content="Intake"
                                                            type="info"
                                                        />
                                                    ) : (
                                                        <Badge
                                                            content="Mantle"
                                                            type="neutral"
                                                        />
                                                    )}
                                                </div>
                                                <span className="text-caption text-textSec">
                                                    {formatDate(
                                                        note.created_at,
                                                        false,
                                                        COMMUNICATION_HISTORY_DATE_FORMAT
                                                    )}
                                                </span>
                                            </div>
                                            {canModifyNote(note) ? (
                                                <div className="flex items-center shrink-0">
                                                    <ActionButton
                                                        variant="can-hover"
                                                        size="auto"
                                                        className="h-7"
                                                        actionTitle="Edit note"
                                                        onClick={() =>
                                                            setNoteInEdit(note)
                                                        }
                                                    >
                                                        <EditIcon />
                                                    </ActionButton>
                                                    <ActionButton
                                                        variant="can-hover"
                                                        size="auto"
                                                        className="h-7"
                                                        actionTitle="Delete note"
                                                        onClick={() =>
                                                            setNoteToDeleteId(
                                                                note.communication_note_id
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon />
                                                    </ActionButton>
                                                </div>
                                            ) : null}
                                        </div>
                                        <p className="text-base whitespace-pre-wrap">
                                            {note.body}
                                        </p>
                                        {note.updated_by_name ? (
                                            <span className="text-caption text-textSec">
                                                {`Edited by ${
                                                    note.updated_by_name
                                                } on ${formatDate(
                                                    note.updated_at,
                                                    false,
                                                    COMMUNICATION_HISTORY_DATE_FORMAT
                                                )}`}
                                            </span>
                                        ) : null}
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <p className="text-base text-textSec mb-[--spacing-header]">
                                No notes yet
                            </p>
                        )}
                        {canAddNote ? (
                            <AddCommunicationNote
                                channel={channel}
                                recordId={recordId}
                                onFormSubmit={handleNotesChange}
                            />
                        ) : null}
                    </>
                </DialogContent>
            </Dialog>
            {noteInEdit ? (
                <Dialog open>
                    <DialogContent
                        title="Edit note"
                        variant="center"
                        handleCloseDialog={() => setNoteInEdit(null)}
                        saveText="Save changes"
                        cancelText="Cancel"
                        isSubmitBtnDisabled={editIsMutating > 0}
                        isCancelBtnDisabled={editIsMutating > 0}
                        submitBtnFormValue="edit-communication-note"
                    >
                        <EditCommunicationNote
                            communicationNote={noteInEdit}
                            onFormSubmit={handleEditComplete}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <AlertDialog open={Boolean(noteToDeleteId)}>
                <AlertDialogContent
                    title="Are you sure you want to delete this note?"
                    description="It will also be removed in Intake. This action cannot be reversed."
                    isDeleting
                    onCancelClick={() => setNoteToDeleteId(null)}
                    saveText="Delete note"
                    cancelText="Cancel"
                    isSaveBtnDisabled={deleteCommunicationNote.isLoading}
                    isCancelBtnDisabled={deleteCommunicationNote.isLoading}
                    onSaveClick={handleDeleteNote}
                />
            </AlertDialog>
        </>
    );
}
